import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ResponsiveList } from "@/components/ui/responsive-list";
import {
  MobileCard,
  MobileCardHeader,
  MobileCardTitle,
  MobileCardSubtitle,
  MobileCardMeta,
  MobileCardMetaItem,
  MobileCardFooter,
} from "@/components/ui/mobile-card";
import {
  ListPageHeader,
  ListPageHeaderContent,
  ListPageHeaderTitle,
  ListPageHeaderDescription,
  ListPageHeaderActions,
  ListPageToolbar,
  ListPageFooter,
} from "@/components/ui/list-page-header";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Edit02Icon, Delete02Icon, Search01Icon, Add01Icon, Upload04Icon, Download01Icon, Settings02Icon, ArrowLeft01Icon, ArrowRight01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { downloadBlob } from "@/lib/utils/file";
import { exportPremiumsCsv, exportPremiumsJson } from "../api/premium-api";
import { formatCurrency } from "@/lib/utils/currency";
import { usePremiums } from "../queries/premium-queries";
import type { PremiumTypeSlug } from "../queries/premium-queries";
import { useDeletePremium, useImportPremiums, useCategories } from "../queries/catalog-queries";
import { CollapseToggle } from "@/features/orders/components/collapse-toggle";
import { useGroupCollapse } from "@/features/orders/components/use-group-collapse";
import { PremiumFormDrawer } from "../components/premium-form-drawer";
import {
  usePremiumStore,
  getAvailablePremiumColumns,
} from "../stores/premiums-store";
import type { PremiumColumnDef } from "../stores/premiums-store";
import type { Premium, Category, DoorStyle, WoodSpecies } from "../types/catalog.types";

// ─── Constants ────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  door_style: "Door Styles",
  wood_species: "Wood Species",
  finish_option: "Finish Options",
  finished_side: "Finished Sides",
};

const TYPE_SINGULAR: Record<string, string> = {
  door_style: "door style",
  wood_species: "wood species",
  finish_option: "finish option",
  finished_side: "finished side",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ─── Page Component ───────────────────────────────────────

interface Props {
  premiumType: PremiumTypeSlug;
}

export default function PremiumListPage({ premiumType }: Props) {
  const isFinishedSide = premiumType === "finished_side";

  // Persisted store for this premium type
  const useStore = usePremiumStore(premiumType);
  const codeFilter = useStore((s) => s.codeFilter);
  const setCodeFilter = useStore((s) => s.setCodeFilter);
  const descFilter = useStore((s) => s.descFilter);
  const setDescFilter = useStore((s) => s.setDescFilter);
  const selectedCatIds = useStore((s) => s.selectedCatIds);
  const toggleCatStore = useStore((s) => s.toggleCat);
  const setSelectedCatIdsStore = useStore((s) => s.setSelectedCatIds);
  const [catSearch, setCatSearch] = useState("");
  const page = useStore((s) => s.page);
  const setPage = useStore((s) => s.setPage);
  const pageSize = useStore((s) => s.pageSize);
  const setPageSize = useStore((s) => s.setPageSize);
  const orderBy = useStore((s) => s.orderBy);
  const setOrderBy = useStore((s) => s.setOrderBy);
  const fileRef = useRef<HTMLInputElement>(null);

  const debouncedCode = useDebounce(codeFilter, 400);
  const debouncedDesc = useDebounce(descFilter, 400);

  // Column visibility
  const availableColumns = useMemo(
    () => getAvailablePremiumColumns(premiumType),
    [premiumType],
  );
  const visibleColumnsArray = useStore((s) => s.visibleColumns);
  const toggleColumnStore = useStore((s) => s.toggleColumn);
  const visibleColumns = useMemo(() => new Set(visibleColumnsArray), [visibleColumnsArray]);
  const visibleCols = availableColumns.filter((c) => visibleColumns.has(c.key));

  // Group collapse
  const [allCollapsed, setAllCollapsed] = useState(false);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPremium, setEditingPremium] = useState<Premium | null>(null);

  // Categories for filter
  const { data: catData } = useCategories();
  const allCategories = (Array.isArray(catData) ? catData : (catData as { results?: Category[] })?.results ?? []) as Category[];

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, page_size: pageSize, order_by: orderBy };
    if (debouncedCode) p.code = debouncedCode;
    if (debouncedDesc) p.description = debouncedDesc;
    if (selectedCatIds.length > 0) p.categories = selectedCatIds.join(",");
    return p;
  }, [page, pageSize, orderBy, debouncedCode, debouncedDesc, selectedCatIds]);

  const { data, isLoading } = usePremiums(premiumType, params);
  const response = data as { results: Premium[]; total: number } | undefined;
  const items = useMemo<Premium[]>(() => response?.results ?? [], [response]);
  const totalCount = response?.total ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Group items
  const grouped = useMemo(() => {
    const map = new Map<string, Premium[]>();
    for (const p of items) {
      const groupKey = p.group_name ?? "Ungrouped";
      const list = map.get(groupKey) ?? [];
      list.push(p);
      map.set(groupKey, list);
    }
    return map;
  }, [items]);

  const deleteMut = useDeletePremium(premiumType);
  const importMut = useImportPremiums(premiumType);

  async function handleExport(format: "csv" | "json") {
    try {
      const exportParams = { ...params };
      delete exportParams.page;
      delete exportParams.page_size;
      const blob = format === "csv"
        ? await exportPremiumsCsv(premiumType, exportParams)
        : await exportPremiumsJson(premiumType, exportParams);
      downloadBlob(blob, `${premiumType}.${format}`);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    importMut.mutate(fd, {
      onSuccess: () => toast.success("Imported successfully"),
      onError: () => toast.error("Import failed"),
    });
    e.target.value = "";
  }

  function handleDelete(id: number) {
    deleteMut.mutate(id, {
      onSuccess: () => toast.success("Deleted"),
      onError: () => toast.error("Delete failed"),
    });
  }

  function openCreate() {
    setEditingPremium(null);
    setDrawerOpen(true);
  }

  function openEdit(item: Premium) {
    setEditingPremium(item);
    setDrawerOpen(true);
  }

  function resetPage() { setPage(1); }

  function toggleSort(col: string) {
    const apiField = col === "price_pct" ? "price" : col;
    setOrderBy(orderBy === apiField ? `-${apiField}` : apiField);
  }

  function sortIndicator(col: string) {
    const apiField = col === "price_pct" ? "price" : col;
    if (orderBy === apiField) return " ↑";
    if (orderBy === `-${apiField}`) return " ↓";
    return "";
  }

  const toggleColumn = toggleColumnStore;
  const toggleCat = toggleCatStore;

  const filteredCats = catSearch
    ? allCategories.filter((c) => c.name.toLowerCase().includes(catSearch.toLowerCase()))
    : allCategories;

  const colSpan = visibleCols.length + 1;

  function getCellValue(item: Premium, key: string): React.ReactNode {
    switch (key) {
      case "code":
        return <span className="font-mono font-medium">{item.code}</span>;
      case "group":
        return <span className="text-muted-foreground">{item.group_name || "—"}</span>;
      case "description":
        return <span className="max-w-[200px] truncate block text-muted-foreground">{item.description || "—"}</span>;
      case "price_pct":
        return item.price ? formatCurrency(item.price) : item.percentage ? `${item.percentage}%` : "—";
      case "export_name":
        return <span className="max-w-[160px] truncate block text-muted-foreground">{item.export_name || "—"}</span>;
      case "categories":
        return (
          <div className="flex gap-1 flex-wrap">
            {item.categories?.map((c) => (
              <Badge key={c.id} variant="secondary" className="text-[10px]">{c.name}</Badge>
            ))}
          </div>
        );
      case "finish_options": {
        const fo = (item as WoodSpecies).finish_options;
        return (
          <div className="flex gap-1 flex-wrap">
            {fo?.map((f) => (
              <Badge key={f.id} variant="secondary" className="text-[10px]">{f.code}</Badge>
            ))}
          </div>
        );
      }
      case "wood_species": {
        const ws = (item as DoorStyle).wood_species;
        return (
          <div className="flex gap-1 flex-wrap">
            {ws?.map((w) => (
              <Badge key={w.id} variant="secondary" className="text-[10px]">{w.code}</Badge>
            ))}
          </div>
        );
      }
      case "images":
        return <span className="text-muted-foreground">{item.images?.length || 0}</span>;
      case "tags":
        return (
          <div className="flex gap-1 flex-wrap">
            {item.tags?.map((t) => (
              <Badge key={t.id} variant="outline" className="text-[10px]">{t.display ?? t.name}</Badge>
            ))}
          </div>
        );
      default:
        return "—";
    }
  }

  return (
    <Card className="!gap-0 !py-0">
      {/* Header */}
      <ListPageHeader>
        <ListPageHeaderContent>
          <ListPageHeaderTitle>{TYPE_LABELS[premiumType] ?? premiumType}</ListPageHeaderTitle>
          <ListPageHeaderDescription>Manage {TYPE_SINGULAR[premiumType]} premiums</ListPageHeaderDescription>
        </ListPageHeaderContent>
        <ListPageHeaderActions>
          <Button variant="outline" size="sm" className="cursor-pointer" onClick={() => handleExport("csv")}>
            <HugeiconsIcon icon={Download01Icon} size={14} className="mr-1.5" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" className="cursor-pointer" onClick={() => handleExport("json")}>
            <HugeiconsIcon icon={Download01Icon} size={14} className="mr-1.5" />
            Export JSON
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <Button variant="outline" size="sm" className="cursor-pointer" onClick={() => fileRef.current?.click()}>
            <HugeiconsIcon icon={Upload04Icon} size={14} className="mr-1.5" />
            Import CSV
          </Button>
          {!isFinishedSide && (
            <Button size="sm" className="cursor-pointer" onClick={openCreate}>
              <HugeiconsIcon icon={Add01Icon} size={14} className="mr-1.5" />
              Add {TYPE_LABELS[premiumType]?.replace(/s$/, "") ?? "Premium"}
            </Button>
          )}
        </ListPageHeaderActions>
      </ListPageHeader>

      {/* Filters */}
      <ListPageToolbar>
        <div className="relative flex-1 min-w-[160px] max-w-[220px]">
          <HugeiconsIcon icon={Search01Icon} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Code"
            value={codeFilter}
            onChange={(e) => { setCodeFilter(e.target.value); resetPage(); }}
            className="pl-9 h-10"
          />
        </div>
        <Input
          placeholder="Description"
          value={descFilter}
          onChange={(e) => { setDescFilter(e.target.value); resetPage(); }}
          className="h-10 w-[200px]"
        />
        <Popover>
          <PopoverTrigger
            className="flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm cursor-pointer min-w-[160px]"
          >
            {selectedCatIds.length === 0 ? (
              <span className="text-muted-foreground">Categories</span>
            ) : (
              <span className="text-sm">{selectedCatIds.length} selected</span>
            )}
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-2" align="start">
            <div className="relative mb-2">
              <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={catSearch}
                onChange={(e) => setCatSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto space-y-0.5">
              {filteredCats.map((cat) => (
                <label key={cat.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50 cursor-pointer text-sm">
                  <Checkbox
                    checked={selectedCatIds.includes(cat.id)}
                    onCheckedChange={() => toggleCat(cat.id)}
                  />
                  {cat.name}
                </label>
              ))}
              {filteredCats.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">No categories found</p>
              )}
            </div>
            {selectedCatIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer w-full mt-1 text-xs"
                onClick={() => setSelectedCatIdsStore([])}
              >
                Clear all
              </Button>
            )}
          </PopoverContent>
        </Popover>

        {/* Column visibility */}
        <Popover>
          <PopoverTrigger className="flex h-10 items-center gap-2 rounded-md border bg-card px-3 text-sm cursor-pointer hover:bg-muted/50 ml-auto">
            <HugeiconsIcon icon={Settings02Icon} size={16} />
            <span>Columns</span>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-1 max-h-80 overflow-y-auto" align="end">
            {availableColumns.map((col) => {
              const checked = visibleColumns.has(col.key);
              return (
                <button
                  key={col.key}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleColumn(col.key)}
                >
                  <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? "bg-primary border-primary" : "border-border"}`}>
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  {col.label}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      </ListPageToolbar>

      {/* Sub-header with collapse toggle */}
      {!isLoading && grouped.size > 0 && (
        <div className="flex items-center justify-between px-8 py-3 border-b">
          <h3 className="text-sm font-semibold">Premiums by Group</h3>
          <CollapseToggle allCollapsed={allCollapsed} onToggle={() => setAllCollapsed((c) => !c)} />
        </div>
      )}

      {/* Table */}
      <ResponsiveList>
      <ResponsiveList.Desktop>
      <Table>
        <TableHeader>
            <TableRow className="border-b">
              {visibleCols.map((col) => (
                <TableHead
                  key={col.key}
                  className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${
                    col.key === "code" ? "pl-8" : ""
                  } ${col.sortable ? "cursor-pointer select-none hover:text-foreground" : ""
                  } ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"}`}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                >
                  {col.label}{col.sortable ? sortIndicator(col.key) : ""}
                </TableHead>
              ))}
              <TableHead className="px-3 py-3 pr-8 w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="py-20 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="py-20 text-center text-sm text-muted-foreground">
                  No {TYPE_SINGULAR[premiumType]} premiums found.
                </TableCell>
              </TableRow>
            ) : (
              [...grouped.entries()].map(([groupName, groupItems]) => (
                <GroupBlock
                  key={groupName}
                  groupName={groupName}
                  items={groupItems}
                  visibleCols={visibleCols}
                  colSpan={colSpan}
                  isFinishedSide={isFinishedSide}
                  defaultCollapsed={allCollapsed}
                  getCellValue={getCellValue}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
        </ResponsiveList.Desktop>

        <ResponsiveList.Mobile>
          {isLoading ? (
            <div className="px-4 py-20 text-center text-sm text-muted-foreground">Loading...</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-20 text-center text-sm text-muted-foreground">
              No {TYPE_SINGULAR[premiumType]} premiums found.
            </div>
          ) : (
            items.map((item) => (
              <MobileCard key={item.id}>
                <MobileCardHeader>
                  <MobileCardTitle>
                    <span className="font-mono">{item.code}</span>
                    {item.description && (
                      <MobileCardSubtitle className="truncate mt-0.5">{item.description}</MobileCardSubtitle>
                    )}
                  </MobileCardTitle>
                  <div className="text-sm font-medium shrink-0">
                    {item.price ? formatCurrency(item.price) : item.percentage ? `${item.percentage}%` : "—"}
                  </div>
                </MobileCardHeader>
                <MobileCardMeta>
                  {item.group_name && <MobileCardMetaItem label="Group" value={item.group_name} />}
                  <MobileCardMetaItem label="Export Name" value={item.export_name || "—"} />
                  <MobileCardMetaItem
                    label="Categories"
                    value={
                      item.categories?.length ? (
                        <div className="flex gap-1 flex-wrap">
                          {item.categories.map((c) => (
                            <Badge key={c.id} variant="secondary" className="text-[10px]">{c.name}</Badge>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )
                    }
                  />
                  {item.tags?.length ? (
                    <MobileCardMetaItem
                      label="Tags"
                      value={
                        <div className="flex gap-1 flex-wrap">
                          {item.tags.map((t) => (
                            <Badge key={t.id} variant="outline" className="text-[10px]">{t.display ?? t.name}</Badge>
                          ))}
                        </div>
                      }
                    />
                  ) : null}
                </MobileCardMeta>
                <MobileCardFooter>
                  <div />
                  <div className="flex gap-0.5">
                    <Button variant="ghost" size="icon" className="cursor-pointer h-7 w-7" onClick={() => openEdit(item)}>
                      <HugeiconsIcon icon={Edit02Icon} size={14} />
                    </Button>
                    {!isFinishedSide && (
                      <AlertDialog>
                        <AlertDialogTrigger render={
                          <Button variant="ghost" size="icon" className="cursor-pointer h-7 w-7 text-destructive" />
                        }>
                          <HugeiconsIcon icon={Delete02Icon} size={14} />
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Premium</AlertDialogTitle>
                            <AlertDialogDescription>
                              Permanently delete &quot;{item.code}&quot;? This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
                            <AlertDialogAction className="cursor-pointer" onClick={() => handleDelete(item.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </MobileCardFooter>
              </MobileCard>
            ))
          )}
        </ResponsiveList.Mobile>
        </ResponsiveList>

      {/* Footer */}
      <ListPageFooter>
        <div className="text-sm text-muted-foreground">
          {totalCount > 0 && <>{totalCount} {totalCount === 1 ? "item" : "items"}</>}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Rows</span>
            <Select value={String(pageSize)} onValueChange={(val) => { if (val) { setPageSize(Number(val)); setPage(1); } }}>
              <SelectTrigger className="w-18 !h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {totalPages > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8 cursor-pointer" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8 cursor-pointer" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
                </Button>
              </div>
            </>
          )}
        </div>
      </ListPageFooter>

      <PremiumFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        premiumType={premiumType}
        premium={editingPremium}
      />
    </Card>
  );
}

// ─── GroupBlock ────────────────────────────────────────────

function GroupBlock({
  groupName, items, visibleCols, colSpan, isFinishedSide, defaultCollapsed, getCellValue, onEdit, onDelete,
}: {
  groupName: string;
  items: Premium[];
  visibleCols: PremiumColumnDef[];
  colSpan: number;
  isFinishedSide: boolean;
  defaultCollapsed: boolean;
  getCellValue: (item: Premium, key: string) => React.ReactNode;
  onEdit: (item: Premium) => void;
  onDelete: (id: number) => void;
}) {
  const [isCollapsed, toggleCollapsed] = useGroupCollapse(defaultCollapsed);

  return (
    <>
      <TableRow
        className="bg-muted/30 border-b cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={toggleCollapsed}
      >
        <TableCell className="px-3 py-3.5 pl-8 w-8">
          <svg
            className={`transition-transform text-muted-foreground ${isCollapsed ? "" : "rotate-90"}`}
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </TableCell>
        <TableCell colSpan={colSpan - 1} className="px-3 py-3.5 text-sm font-medium">
          {groupName}
          <Badge variant="secondary" className="ml-2 text-[10px]">{items.length}</Badge>
        </TableCell>
      </TableRow>
      {!isCollapsed && items.map((item) => (
        <TableRow key={item.id} className="border-b border-border/40 hover:bg-muted/10 transition-colors">
          {visibleCols.map((col) => (
            <TableCell
              key={col.key}
              className={`px-3 py-2.5 text-sm ${
                col.key === "code" ? "pl-8" : ""
              } ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}`}
            >
              {getCellValue(item, col.key)}
            </TableCell>
          ))}
          <TableCell className="px-3 py-2.5 pr-8 text-right w-20">
            <div className="flex justify-end gap-0.5">
              <Button variant="ghost" size="icon" className="cursor-pointer h-7 w-7" onClick={() => onEdit(item)}>
                <HugeiconsIcon icon={Edit02Icon} size={14} />
              </Button>
              {!isFinishedSide && (
                <AlertDialog>
                  <AlertDialogTrigger render={
                    <Button variant="ghost" size="icon" className="cursor-pointer h-7 w-7 text-destructive" />
                  }>
                    <HugeiconsIcon icon={Delete02Icon} size={14} />
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Premium</AlertDialogTitle>
                      <AlertDialogDescription>
                        Permanently delete &quot;{item.code}&quot;? This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="cursor-pointer" onClick={() => onDelete(item.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ─── Wrapper components for each route ────────────────────

export function DoorStylesPage() {
  return <PremiumListPage premiumType="door_style" />;
}

export function WoodSpeciesPage() {
  return <PremiumListPage premiumType="wood_species" />;
}

export function FinishOptionsPage() {
  return <PremiumListPage premiumType="finish_option" />;
}

export function FinishedSidesPage() {
  return <PremiumListPage premiumType="finished_side" />;
}
